import { debugLog } from '../utils/debugUtils';
import CardTracker from './CardTracker';

/**
 * AIService - Centralized AI decision-making for Ronda and Baciga.
 *
 * All bot decisions are made here, taking into account the difficulty level.
 * This service receives pure game state as input and returns decisions as output,
 * with no UI side effects.
 *
 * Difficulty rules:
 * - EASY / HARD: Only rivals (players 1, 3) use the selected difficulty.
 *   The partner (player 2) always uses HARD logic.
 * - NORMAL: All bots use NORMAL.
 */
class AIService {
    /**
     * @param {Object} rondaLogic - Reference to the RondaLogic instance
     * @param {Object} bacigaLogic - Reference to the BacigaLogic instance
     */
    constructor(rondaLogic = null, bacigaLogic = null) {
        this.rondaLogic = rondaLogic;
        this.bacigaLogic = bacigaLogic;
        this.difficulty = 'NORMAL';
        this.cardTracker = new CardTracker();
    }

    // ─── Configuration ─────────────────────────────────────────────

    setDifficulty(difficulty) {
        this.difficulty = difficulty || 'NORMAL';
        debugLog(`🤖 [AIService] Difficulty set to: ${this.difficulty}`);
    }

    setRondaLogic(rondaLogic) {
        this.rondaLogic = rondaLogic;
    }

    setBacigaLogic(bacigaLogic) {
        this.bacigaLogic = bacigaLogic;
    }

    /**
     * Resolve the effective difficulty for a given player.
     * Player 0 = human (should never call this).
     * Player 2 = partner (always HARD in EASY/HARD mode).
     * Players 1, 3 = rivals (use the selected difficulty in EASY/HARD mode).
     * In NORMAL mode, everyone uses NORMAL.
     *
     * @param {number} playerIndex - 0-3
     * @returns {string} 'EASY' | 'NORMAL' | 'HARD'
     */
    getEffectiveDifficulty(playerIndex) {
        if (this.difficulty === 'NORMAL') return 'NORMAL';

        // EASY or HARD: partner always uses HARD, rivals use the selected difficulty
        const isPartner = (playerIndex === 2);
        return isPartner ? 'HARD' : this.difficulty;
    }

    /**
     * Reset card tracker. Call at the start of each new round (deck reshuffle).
     */
    resetCardTracker() {
        this.cardTracker.reset();
        debugLog('🤖 [AIService] Card tracker reset');
    }

    // ═══════════════════════════════════════════════════════════════
    // ─── RONDA AI DECISIONS ────────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════

    /**
     * Decide which card to play during a Ronda turn.
     *
     * @param {number} playerIndex
     * @param {Array} playerCards - Cards in the player's hand
     * @param {Array} centerCards - Cards currently in the center
     * @param {Object} gameState - { lastDroppedValue, isLastHand, teamRondaValue, rivalRondaValues }
     * @returns {{ card: Object, reason: string }}
     */
    decideCardToPlay(playerIndex, playerCards, centerCards, gameState = {}) {
        const diff = this.getEffectiveDifficulty(playerIndex);

        if (diff === 'EASY') {
            return this._decideCardToPlay_Easy(playerIndex, playerCards, centerCards, gameState);
        } else if (diff === 'HARD') {
            return this._decideCardToPlay_Hard(playerIndex, playerCards, centerCards, gameState);
        }
        return this._decideCardToPlay_Normal(playerIndex, playerCards, centerCards, gameState);
    }

    // --- NORMAL (port of current behavior + "throw highest when dropping") ---
    _decideCardToPlay_Normal(playerIndex, playerCards, centerCards, gameState) {
        // Strategy 1: Check for Aseo (card that clears the board)
        for (const card of playerCards) {
            const result = this.rondaLogic.processMove(card, centerCards);
            if (result.action === 'COLLECT' && result.centerCardsToRemove.length === centerCards.length) {
                debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} found Aseo with:`, card);
                return { card, reason: 'aseo' };
            }
        }

        // Strategy 2: Check for Caída (same value as last dropped)
        if (gameState.lastDroppedValue !== null && gameState.lastDroppedValue !== undefined) {
            const caidaCard = playerCards.find(c => c.value === gameState.lastDroppedValue);
            if (caidaCard) {
                const result = this.rondaLogic.processMove(caidaCard, centerCards);
                if (result.action === 'COLLECT') {
                    debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} found Caída with:`, caidaCard);
                    return { card: caidaCard, reason: 'caida' };
                }
            }
        }

        // Strategy 3: Prefer collecting over dropping
        for (const card of playerCards) {
            const result = this.rondaLogic.processMove(card, centerCards);
            if (result.action === 'COLLECT') {
                debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} collecting with:`, card);
                return { card, reason: 'collect' };
            }
        }

        // Fallback: Drop the card with the highest value (LOGICA_IA: "lanza carta de mayor valor")
        const sorted = [...playerCards].sort((a, b) => b.value - a.value);
        debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} dropping highest:`, sorted[0]);
        return { card: sorted[0], reason: 'drop_highest' };
    }

    // --- EASY ---
    _decideCardToPlay_Easy(playerIndex, playerCards, centerCards, gameState) {
        // EASY: 20% of the time, play a random card even when they can collect
        // Do NOT seek aseos. Avoid caídas.
        const collectableCards = playerCards.filter(card => {
            const result = this.rondaLogic.processMove(card, centerCards);
            return result.action === 'COLLECT';
        });

        if (collectableCards.length > 0) {
            // 20% chance to "miss" and play a different card
            if (Math.random() < 0.2) {
                const nonCollectable = playerCards.filter(c => !collectableCards.includes(c));
                if (nonCollectable.length > 0) {
                    const randomCard = nonCollectable[Math.floor(Math.random() * nonCollectable.length)];
                    debugLog(`🤖 [AI-EASY] Player ${playerIndex} intentionally misses collect, plays:`, randomCard);
                    return { card: randomCard, reason: 'easy_miss' };
                }
            }
            // Normal collect (no aseo preference, no caída preference)
            const randomCollect = collectableCards[Math.floor(Math.random() * collectableCards.length)];
            debugLog(`🤖 [AI-EASY] Player ${playerIndex} collecting with:`, randomCollect);
            return { card: randomCollect, reason: 'collect' };
        }

        // Cannot collect: drop random card
        const randomIndex = Math.floor(Math.random() * playerCards.length);
        debugLog(`🤖 [AI-EASY] Player ${playerIndex} random drop:`, playerCards[randomIndex]);
        return { card: playerCards[randomIndex], reason: 'random' };
    }

    // --- HARD ---
    _decideCardToPlay_Hard(playerIndex, playerCards, centerCards, gameState) {
        const { isLastHand, teamRondaValue, dealerIndex } = gameState;
        const isDealer = playerIndex === dealerIndex;

        // Last hand as dealer: save the ace (1) or a collecting card for the sweep
        if (isLastHand && isDealer && playerCards.length > 1) {
            const ace = playerCards.find(c => c.value === 1);
            if (ace) {
                // Play a different card, saving the ace for last
                const otherCards = playerCards.filter(c => c !== ace);
                if (otherCards.length > 0) {
                    // Among others, pick the best play
                    const bestOther = this._pickBestCollectOrSafeDrop(playerIndex, otherCards, centerCards, gameState);
                    debugLog(`🤖 [AI-HARD] Player ${playerIndex} saving ace, plays:`, bestOther.card);
                    return bestOther;
                }
            }
        }

        // Strategy 1: Aseo (always take it in HARD)
        for (const card of playerCards) {
            const result = this.rondaLogic.processMove(card, centerCards);
            if (result.action === 'COLLECT' && result.centerCardsToRemove.length === centerCards.length) {
                debugLog(`🤖 [AI-HARD] Player ${playerIndex} takes Aseo with:`, card);
                return { card, reason: 'aseo' };
            }
        }

        // Strategy 2: Caída (always take it)
        if (gameState.lastDroppedValue !== null && gameState.lastDroppedValue !== undefined) {
            const caidaCard = playerCards.find(c => c.value === gameState.lastDroppedValue);
            if (caidaCard) {
                const result = this.rondaLogic.processMove(caidaCard, centerCards);
                if (result.action === 'COLLECT') {
                    debugLog(`🤖 [AI-HARD] Player ${playerIndex} takes Caída with:`, caidaCard);
                    return { card: caidaCard, reason: 'caida' };
                }
            }
        }

        // Strategy 3: Maximize number of cards collected
        let bestCollect = null;
        let bestCollectCount = 0;
        for (const card of playerCards) {
            const result = this.rondaLogic.processMove(card, centerCards);
            if (result.action === 'COLLECT') {
                const count = result.collectedCards.length;
                if (count > bestCollectCount) {
                    bestCollectCount = count;
                    bestCollect = card;
                }
            }
        }

        // If partner has ronda, don't play cards below ronda value (prevents rivals collecting)
        if (bestCollect && teamRondaValue) {
            if (bestCollect.value < teamRondaValue) {
                // Check if we have a better option that doesn't risk the ronda
                const safeCollects = playerCards.filter(card => {
                    if (card.value < teamRondaValue) return false;
                    const result = this.rondaLogic.processMove(card, centerCards);
                    return result.action === 'COLLECT';
                });
                if (safeCollects.length > 0) {
                    bestCollect = safeCollects[0];
                    debugLog(`🤖 [AI-HARD] Player ${playerIndex} protects partner ronda (${teamRondaValue}), collects with:`, bestCollect);
                }
            }
        }

        if (bestCollect) {
            debugLog(`🤖 [AI-HARD] Player ${playerIndex} max collect (${bestCollectCount} cards) with:`, bestCollect);
            return { card: bestCollect, reason: 'collect' };
        }

        // Strategy 4: Smart drop - pick card with lowest caída risk, avoid aseos
        return this._pickSafeDrop(playerIndex, playerCards, centerCards);
    }

    /**
     * Helper: pick the best collect or safest drop from a subset of cards.
     */
    _pickBestCollectOrSafeDrop(playerIndex, cards, centerCards, gameState) {
        // Try collecting first
        let bestCollect = null;
        let bestCount = 0;
        for (const card of cards) {
            const result = this.rondaLogic.processMove(card, centerCards);
            if (result.action === 'COLLECT' && result.collectedCards.length > bestCount) {
                bestCount = result.collectedCards.length;
                bestCollect = card;
            }
        }
        if (bestCollect) {
            return { card: bestCollect, reason: 'collect' };
        }
        return this._pickSafeDrop(playerIndex, cards, centerCards);
    }

    /**
     * Helper: pick the safest card to drop (lowest caída risk, avoid leaving aseo).
     */
    _pickSafeDrop(playerIndex, playerCards, centerCards) {
        let bestCard = playerCards[0];
        let bestScore = -Infinity;

        for (const card of playerCards) {
            let score = 0;
            const caidaRisk = this.cardTracker.getCaidaRisk(card.value);
            // Lower risk = better (invert)
            score -= caidaRisk * 100;

            // Avoid leaving aseo for the next player
            if (this.cardTracker.wouldLeaveAseo(card, centerCards)) {
                score -= 200;
            }

            // Prefer dropping higher value cards (less useful in hand)
            score += card.value * 0.5;

            if (score > bestScore) {
                bestScore = score;
                bestCard = card;
            }
        }

        debugLog(`🤖 [AI-HARD] Player ${playerIndex} safe drop:`, bestCard);
        return { card: bestCard, reason: 'safe_drop' };
    }

    // ─── RONDA: ENVÍO DECISION ─────────────────────────────────────

    /**
     * Decide how to handle an envío.
     *
     * @param {number} playerIndex
     * @param {Object|null} aiDeclaration - This player's declaration { type, value, ... }
     * @param {Object} gameState - { hasPendingEnvio, availableDecisions, manoPlayerIndex }
     * @returns {{ decision: string }}
     */
    decideEnvio(playerIndex, aiDeclaration, gameState = {}) {
        const diff = this.getEffectiveDifficulty(playerIndex);
        const { hasPendingEnvio, availableDecisions } = gameState;

        if (diff === 'EASY') {
            return this._decideEnvio_Easy(hasPendingEnvio, availableDecisions);
        } else if (diff === 'HARD') {
            return this._decideEnvio_Hard(playerIndex, aiDeclaration, gameState);
        }
        return this._decideEnvio_Normal(aiDeclaration, hasPendingEnvio, availableDecisions);
    }

    _decideEnvio_Easy(hasPendingEnvio, availableDecisions) {
        // EASY: Always reject envío, always chiquita when sending
        if (hasPendingEnvio) {
            return { decision: 'no_acepto' };
        }
        // If chiquita is not available (last player), fall back to a_la_mayor
        if (availableDecisions && !availableDecisions.includes('chiquita')) {
            return { decision: 'a_la_mayor' };
        }
        return { decision: 'chiquita' };
    }

    _decideEnvio_Normal(aiDeclaration, hasPendingEnvio, availableDecisions) {
        // NORMAL: Port of current probabilistic logic
        const random = Math.random();

        if (hasPendingEnvio) {
            // Someone has already sent - accept or reject
            if (aiDeclaration && aiDeclaration.value >= 10) {
                return { decision: 'acepto' };
            }
            return { decision: random < 0.4 ? 'acepto' : 'no_acepto' };
        }

        // No pending envío
        if (availableDecisions && availableDecisions.includes('chiquita')) {
            if (random < 0.4) {
                return { decision: 'enviar' };
            }
            return { decision: 'chiquita' };
        }

        // Last player - enviar or a_la_mayor
        if (aiDeclaration && aiDeclaration.value >= 10) {
            return { decision: random < 0.7 ? 'enviar' : 'a_la_mayor' };
        }
        return { decision: random < 0.3 ? 'enviar' : 'a_la_mayor' };
    }

    _decideEnvio_Hard(playerIndex, aiDeclaration, gameState) {
        const { hasPendingEnvio, availableDecisions } = gameState;

        if (hasPendingEnvio) {
            // Use card tracker: estimate if our ronda value is strong enough
            if (aiDeclaration) {
                const rondaValue = aiDeclaration.value;
                // Check how many unseen cards of higher value exist
                const unseenCards = this.cardTracker.getUnseenCards();
                const higherUnseenCount = unseenCards.filter(c => c.value > rondaValue).length;
                const totalUnseen = unseenCards.length;

                // If few higher cards remain unseen, accept (our ronda is likely strong)
                const risk = totalUnseen > 0 ? higherUnseenCount / totalUnseen : 0;
                if (risk < 0.3 || aiDeclaration.type === 'rondin') {
                    return { decision: 'acepto' };
                }
            }
            return { decision: 'no_acepto' };
        }

        // No pending envío - decide whether to send
        if (aiDeclaration) {
            const rondaValue = aiDeclaration.value;
            // Strong ronda (high value or rondín): always send
            if (aiDeclaration.type === 'rondin' || rondaValue >= 10) {
                return { decision: 'enviar' };
            }

            // Medium-strength ronda: check card tracker
            const remaining = this.cardTracker.getRemainingCountByValue(rondaValue);
            // If few remaining of our value (others don't have matching), send
            if (remaining <= 1) {
                return { decision: 'enviar' };
            }

            if (availableDecisions && availableDecisions.includes('chiquita')) {
                return { decision: 'chiquita' };
            }
            return { decision: 'a_la_mayor' };
        }

        // No declaration (shouldn't happen in envío, but fallback)
        if (availableDecisions && availableDecisions.includes('chiquita')) {
            return { decision: 'chiquita' };
        }
        return { decision: 'a_la_mayor' };
    }

    // ─── RONDA: CONTRACAÍDA DECISION ───────────────────────────────

    /**
     * Decide whether to take a contracaída opportunity.
     *
     * @param {number} playerIndex
     * @param {Array} matchingCards - Cards in hand that match the dropped value
     * @param {Object} gameState - (reserved for future use)
     * @returns {{ shouldContracaida: boolean, card: Object|null }}
     */
    decideContracaida(playerIndex, matchingCards, gameState = {}) {
        const diff = this.getEffectiveDifficulty(playerIndex);

        if (!matchingCards || matchingCards.length === 0) {
            return { shouldContracaida: false, card: null };
        }

        if (diff === 'EASY') {
            // EASY: Never take contracaída
            debugLog(`🤖 [AI-EASY] Player ${playerIndex} skips contracaída`);
            return { shouldContracaida: false, card: null };
        }

        if (diff === 'HARD') {
            // HARD: Strategic - take it unless San Vicente risk is very high.
            // unseenOfValue includes our own card(s), so subtract matchingCards.length
            // to get how many OTHER unknown cards of this value remain in play.
            // Only skip if 2+ OTHER unseen cards exist (very high San Vicente risk).
            const cardValue = matchingCards[0].value;
            const unseenOfValue = this.cardTracker.getRemainingCountByValue(cardValue);
            const unseenExcludingOwn = unseenOfValue - matchingCards.length;
            if (unseenExcludingOwn >= 2) {
                debugLog(`🤖 [AI-HARD] Player ${playerIndex} skips contracaída (San Vicente risk: ${unseenExcludingOwn} unseen excluding own)`);
                return { shouldContracaida: false, card: null };
            }
        }

        // NORMAL and HARD (low risk): Always take it
        debugLog(`🤖 [AI-${diff}] Player ${playerIndex} takes contracaída`);
        return { shouldContracaida: true, card: matchingCards[0] };
    }


    // ═══════════════════════════════════════════════════════════════
    // ─── BACIGA AI DECISIONS ───────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════

    /**
     * Decide how to vote in the Baciga declaration phase.
     *
     * @param {number} playerIndex
     * @param {Array} cards - Player's hand
     * @param {Object} score - Result from BacigaLogic.calculateScore()
     * @param {Object} gameState - { handNumber, team1Score, team2Score, allPassedTwice }
     * @returns {string} 'baciga' | 'bacigote' | 'cartas' | 'paso'
     */
    decideVote(playerIndex, cards, score, gameState = {}) {
        const diff = this.getEffectiveDifficulty(playerIndex);
        const { handNumber } = gameState;

        if (diff === 'EASY') {
            return this._decideVote_Easy(score);
        } else if (diff === 'HARD') {
            return this._decideVote_Hard(playerIndex, cards, score, gameState);
        }
        return this._decideVote_Normal(score, handNumber);
    }

    _decideVote_Easy(score) {
        // EASY: Always call báciga if ≤9, never seek bacigote, never vote cartas
        if (score.isBaciga) return 'baciga';
        return 'paso';
    }

    _decideVote_Normal(score, handNumber) {
        // NORMAL: Port of current logic
        if (score.isBacigote) return 'bacigote';
        if (score.isBaciga) return 'baciga';
        if (handNumber > 1 && score.points >= 28) return 'cartas';
        return 'paso';
    }

    _decideVote_Hard(playerIndex, cards, score, gameState) {
        const { handNumber, team1Score, team2Score, allPassedTwice } = gameState;
        const isTeam1 = (playerIndex === 0 || playerIndex === 2);
        const myTeamScore = isTeam1 ? team1Score : team2Score;
        const rivalTeamScore = isTeam1 ? team2Score : team1Score;

        // If all 4 players passed twice in this round, vote cartas next
        if (allPassedTwice && handNumber > 1) {
            debugLog(`🤖 [AI-HARD] Player ${playerIndex} votes cartas after double pass`);
            return 'cartas';
        }

        // Bacigote: declare it, UNLESS rival is close to 18 and we're far behind,
        // in which case we might want Las Cosas instead (vote cartas to go to phase 31)
        if (score.isBacigote) {
            if (rivalTeamScore >= 14 && myTeamScore <= 8 && handNumber > 1) {
                // Rival close to winning, we're far behind.
                // Bacigote = 7 pts, Las Cosas = 8 pts. Try Las Cosas via phase 31.
                debugLog(`🤖 [AI-HARD] Player ${playerIndex} skips bacigote to seek Las Cosas (score: ${myTeamScore} vs ${rivalTeamScore})`);
                return 'cartas';
            }
            return 'bacigote';
        }

        // Báciga: call based on team score proximity to 18
        if (score.isBaciga) {
            // If we're close to 18, always call to close the round
            if (myTeamScore >= 14) {
                return 'baciga';
            }
            // If rival is close, also call to get points before they win
            if (rivalTeamScore >= 14) {
                return 'baciga';
            }
            // Otherwise, standard call
            return 'baciga';
        }

        // No báciga/bacigote: consider cartas
        if (handNumber > 1 && score.points >= 28) {
            return 'cartas';
        }

        return 'paso';
    }

    // ─── BACIGA: DISCARD SELECTION ─────────────────────────────────

    /**
     * Select which cards to discard when passing in Baciga voting.
     *
     * @param {number} playerIndex
     * @param {Array} cards - Player's hand
     * @param {Object} score - Result from BacigaLogic.calculateScore()
     * @param {Object} gameState - { handNumber }
     * @returns {number[]} Indices of cards to discard
     */
    selectDiscards(playerIndex, cards, score, gameState = {}) {
        const diff = this.getEffectiveDifficulty(playerIndex);

        if (diff === 'EASY') {
            return this._selectDiscards_Easy(cards);
        }
        // NORMAL and HARD use the same discard logic (per LOGICA_IA.md)
        return this._selectDiscards_NormalHard(playerIndex, cards, score, gameState);
    }

    _selectDiscards_Easy(cards) {
        // EASY: Discard cards >= 4, pass pairs (don't seek bacigote)
        const indices = [];
        cards.forEach((card, idx) => {
            if (this.bacigaLogic && this.bacigaLogic.isComodin(card)) return; // Keep comodines
            const numVal = parseInt(card.value, 10);
            if (numVal >= 4) {
                indices.push(idx);
            }
        });

        // Must discard at least 1
        if (indices.length === 0) {
            // All cards are < 4 or comodines. Discard the highest non-comodin.
            let maxVal = -1;
            let maxIdx = -1;
            cards.forEach((card, idx) => {
                if (this.bacigaLogic && this.bacigaLogic.isComodin(card)) return;
                const numVal = parseInt(card.value, 10);
                if (numVal > maxVal) {
                    maxVal = numVal;
                    maxIdx = idx;
                }
            });
            if (maxIdx >= 0) indices.push(maxIdx);
        }

        debugLog(`🤖 [AI-EASY] Player discarding indices:`, indices);
        return indices;
    }

    _selectDiscards_NormalHard(playerIndex, cards, score, gameState) {
        // NORMAL/HARD: Discard cards > 3, keep pairs, never discard comodín
        const bacigaLogic = this.bacigaLogic;
        const handNumber = gameState.handNumber || 0;

        // Find pairs (potential Bacigote)
        const valueCounts = {};
        cards.forEach((card, idx) => {
            if (bacigaLogic && !bacigaLogic.isComodin(card)) {
                const val = card.value;
                if (!valueCounts[val]) valueCounts[val] = [];
                valueCounts[val].push(idx);
            }
        });

        // Check for high pairs (value > 3)
        const highPairs = Object.entries(valueCounts)
            .filter(([val, indices]) => indices.length >= 2 && parseInt(val) > 3);

        // Decide if AI will keep pair for Bacigote
        const KEEP_PAIR_CHANCE = 0.25;
        const canKeepPair = handNumber <= 2 && highPairs.length > 0;
        const willKeepPair = canKeepPair && Math.random() < KEEP_PAIR_CHANCE;
        const pairIndicesToKeep = willKeepPair ? highPairs[0][1] : [];

        // Evaluate each card for discard priority
        const cardEvaluations = cards.map((card, idx) => {
            const isComodin = bacigaLogic ? bacigaLogic.isComodin(card) : false;
            const value = bacigaLogic ? bacigaLogic.getCardValue(card) : parseInt(card.value, 10);
            const numericValue = parseInt(card.value) || value;
            const isInKeptPair = pairIndicesToKeep.includes(idx);

            let discardPriority;
            if (isComodin) {
                discardPriority = -100; // Never discard
            } else if (isInKeptPair) {
                discardPriority = -50; // Keep for Bacigote
            } else if (numericValue > 3) {
                discardPriority = value + 10; // High cards get high discard priority
            } else {
                discardPriority = value; // Low cards, lower priority
            }

            return { index: idx, card, isComodin, value, numericValue, isInKeptPair, discardPriority };
        });

        // Sort by priority descending (highest = discard first)
        cardEvaluations.sort((a, b) => b.discardPriority - a.discardPriority);

        // Count "bad" cards
        const badCards = cardEvaluations.filter(e =>
            !e.isComodin && !e.isInKeptPair && e.numericValue > 3
        );

        let numToDiscard;
        if (badCards.length >= 3) {
            numToDiscard = 3;
        } else if (badCards.length >= 2) {
            numToDiscard = 2;
        } else {
            numToDiscard = Math.max(1, badCards.length);
        }

        const discardableCards = cardEvaluations.filter(e => e.discardPriority > 0);
        if (discardableCards.length < numToDiscard) {
            numToDiscard = Math.max(1, discardableCards.length);
            if (numToDiscard === 0) numToDiscard = 1;
        }

        const toDiscard = cardEvaluations
            .filter(e => e.discardPriority > -100) // Never discard comodines
            .slice(0, numToDiscard)
            .map(e => e.index);

        // Ensure at least 1
        if (toDiscard.length === 0) {
            const nonComodin = cardEvaluations.find(e => !e.isComodin);
            if (nonComodin) toDiscard.push(nonComodin.index);
        }

        debugLog(`🤖 [AI] Player ${playerIndex} discarding ${toDiscard.length} cards at indices:`, toDiscard);
        return toDiscard;
    }

    // ─── BACIGA: CARD HIDING ───────────────────────────────────────

    /**
     * Select which card to hide at the start of Las 31 turn.
     *
     * @param {number} playerIndex
     * @param {Array} cards - Player's hand
     * @returns {number|null} Index of card to hide, or null to reveal all
     */
    selectCardToHide(playerIndex, cards) {
        const diff = this.getEffectiveDifficulty(playerIndex);

        if (diff === 'EASY') {
            debugLog(`🤖 [AI-EASY] Player ${playerIndex} reveals all cards`);
            return null;
        }

        if (diff === 'NORMAL') {
            if (Math.random() > 0.2) {
                debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} reveals all cards (80% chance)`);
                return null;
            }
            const randomIndex = Math.floor(Math.random() * cards.length);
            debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} hides random card at index ${randomIndex}`);
            return randomIndex;
        }

        // HARD: Always hide strategically (comodín > highest value card)
        let bestIndex = 0;
        let bestScore = -1;

        cards.forEach((card, idx) => {
            let score = 0;
            if (this.bacigaLogic && this.bacigaLogic.isComodin(card)) {
                score = 100; // Comodines are most valuable to hide
            } else {
                score = this.bacigaLogic ? this.bacigaLogic.getCardValue(card) : parseInt(card.value, 10);
            }

            if (score > bestScore) {
                bestScore = score;
                bestIndex = idx;
            }
        });

        debugLog(`🤖 [AI-HARD] Player ${playerIndex} hides card at index ${bestIndex} (strategic)`);
        return bestIndex;
    }

    // ─── BACIGA: WILDCARD ASSIGNMENT ───────────────────────────────

    /**
     * Get optimal wildcard value for báciga/bacigote declaration phase.
     * Delegates to BacigaLogic.getOptimalWildcardValue().
     *
     * @param {Object} card - The wildcard card
     * @param {Array} currentHand - All cards in hand
     * @returns {number}
     */
    assignWildcardForDeclaration(card, currentHand) {
        if (!this.bacigaLogic) return 1;
        return this.bacigaLogic.getOptimalWildcardValue(card, currentHand);
    }

    /**
     * Get optimal wildcard value for Las 31 phase.
     * Delegates to BacigaLogic.getOptimalWildcardValueForLas31() with effective difficulty.
     *
     * @param {number} playerIndex
     * @param {Object} card - The wildcard card
     * @param {Array} currentHand - All cards in hand
     * @param {boolean} someoneHas31
     * @returns {number}
     */
    assignWildcardForLas31(playerIndex, card, currentHand, someoneHas31 = false) {
        if (!this.bacigaLogic) return 1;
        const diff = this.getEffectiveDifficulty(playerIndex);
        return this.bacigaLogic.getOptimalWildcardValueForLas31(card, currentHand, diff, someoneHas31);
    }

    // ─── BACIGA: HIT OR STAND (LAS 31) ────────────────────────────

    /**
     * Decide whether to hit (pedir carta) or stand (plantarse) in Las 31.
     *
     * @param {number} playerIndex
     * @param {Array} cards - Player's current hand
     * @param {Object} score - Result from BacigaLogic.calculateScore()
     * @param {Object} gameState - { someoneHas31 }
     * @returns {string} 'carta' | 'planto'
     */
    decideHitOrStand(playerIndex, cards, score, gameState = {}) {
        const diff = this.getEffectiveDifficulty(playerIndex);

        // Universal rules
        if (score.points === 31) return 'planto'; // Perfect score
        if (score.points > 31) return 'planto'; // Busted

        if (diff === 'EASY') {
            return this._decideHitOrStand_Easy(score);
        } else if (diff === 'HARD') {
            return this._decideHitOrStand_Hard(playerIndex, cards, score, gameState);
        }
        return this._decideHitOrStand_Normal(playerIndex, cards, score, gameState);
    }

    _decideHitOrStand_Easy(score) {
        // EASY: Hit if minPoints < 25. Using minPoints avoids standing on an inflated
        // score caused by a comodín's optimal value (e.g. 19 + comodín → points=29 but min=20).
        if (score.minPoints < 25) {
            return 'carta';
        }
        return 'planto';
    }

    _decideHitOrStand_Normal(playerIndex, cards, score, gameState) {
        // NORMAL: Port of current logic (without card history consideration)
        const isSafeToHit = score.minPoints <= 21;
        const isGreatScore = score.points >= 29;

        // If an active (unbound) comodín is present, score.points may be inflated by its
        // optimal value (e.g. 19 + comodín → points=29 but min=20). Drawing is still safe
        // and may yield 31 or a ligue, so we hit whenever minPoints is safe regardless of
        // whether the optimal score looks "great".
        const hasActiveComodin = this.bacigaLogic && cards.some(c =>
            this.bacigaLogic.isComodin(c) && !this.bacigaLogic.isCardBound(c)
        );

        if (isSafeToHit && (!isGreatScore || hasActiveComodin)) {
            debugLog(`🤖 [AI-NORMAL] Player ${playerIndex} safe hit: min=${score.minPoints}, optimal=${score.points}${hasActiveComodin ? ' (comodín active)' : ''}`);
            return 'carta';
        }

        // Standard logic for non-flexible hands
        if (score.points >= 27) {
            return 'planto';
        }
        return 'carta';
    }

    _decideHitOrStand_Hard(playerIndex, cards, score, gameState) {
        // HARD: Aggressive with wildcard flexibility, considers card history
        const isSafeToHit = score.minPoints <= 21;

        if (isSafeToHit) {
            // If we have a wildcard and it's safe (min ≤ 21), always hit to chase 31/ligues
            debugLog(`🤖 [AI-HARD] Player ${playerIndex} aggressive hit: min=${score.minPoints}, optimal=${score.points}`);
            return 'carta';
        }

        // Without wildcard flexibility
        // Check for ligues/trios opportunity - if few and might get Las Cosas, hit
        if (this.bacigaLogic && score.points <= 21) {
            const pairsInfo = this.bacigaLogic.countPairsAndTrios(cards);
            if (pairsInfo.pairs > 0 || pairsInfo.trios > 0) {
                // Has some ligues, might improve with another card
                debugLog(`🤖 [AI-HARD] Player ${playerIndex} hits for ligues improvement`);
                return 'carta';
            }
        }

        // Stand on 27+
        if (score.points >= 27) {
            return 'planto';
        }
        return 'carta';
    }
}

export default AIService;
